import { ReplicaTransportError } from '@w/db-turso-browser-adapter/worker'
import { RpcError } from '@w/rpc'
import { toastManager } from '@w/ui/components/toast'
import { ReplicationInvariantError } from '@w/db-replication'
import type { Tel } from '@w/tel'

/** Notification episodes belong to the UI; this reporter owns no round, retry or transport work. */
export function createReplicationErrorReporter(tel: Tel, workspaceId: string) {
  let reported = false
  let offline = false
  return {
    success() { reported = false; offline = false },
    failure(error: unknown, diagnosticOwned = false) {
      if (error instanceof ReplicationInvariantError) return
      const attrs = { operation: 'workspace.replication.sync', workspaceId }
      if (isReplicationOffline(error)) {
        if (!offline) tel.warn('Workspace replication offline', attrs)
        offline = true
      } else if (isReplicationOutcome(error) && !(error instanceof ReplicaTransportError && error.status === 403) && !(error instanceof RpcError && error.status === 403)) {
        if (!reported) {
          reported = true
          tel.warn('Workspace replication refused', attrs)
          // Source: libs/platform/infra/db-replication/docs/replication-semantics.md:268
          // An access rejection (410) re-downloads automatically; the user is only told when nothing recovers the device.
          if (error instanceof ReplicaTransportError && (error.code === 'generation_reset' || error.code === 'eligibility_expired') && !isWorkspaceAccessRejection(error)) {
            toastManager.add({ type: 'error', title: 'Device bootstrap required', description: 'This device is no longer admitted. Local changes remain saved. Create a fresh device replica before synchronizing again.' })
          }
        }
      } else {
        if (!diagnosticOwned && !(error instanceof RpcError)) tel.error(error, attrs)
        if (!reported) {
          reported = true
          toastManager.add({ type: 'error', title: 'Workspace sync failed', description: error instanceof Error ? error.message : String(error) })
        }
      }
    },
  }
}

export function isReplicationOffline(error: unknown): boolean {
  return error instanceof ReplicaTransportError && error.status === 0
}
/** Retry transport interruptions only; refusals and invariant failures remain terminal. */
export function isReplicationTransient(error: unknown): boolean {
  if (error instanceof ReplicationInvariantError) return false
  if (error instanceof ReplicaTransportError || error instanceof RpcError) return error.status === 0 || error.status >= 500
  return error instanceof Error && (error.name === 'TimeoutError' || ('code' in error && error.code === 'ETIMEDOUT'))
}
export function isReplicationOutcome(error: unknown): boolean {
  return (error instanceof ReplicaTransportError || error instanceof RpcError) && error.status >= 400 && error.status < 500
}

/** Row rejection says nothing about the caller's workspace membership. */
export function isWorkspaceAccessRejection(error: unknown): boolean {
  return error instanceof ReplicaTransportError && error.status === 410
}
